import React from 'react';
import { createRoot } from 'react-dom/client';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, X, Info } from 'lucide-react';

class ToastManager {
    constructor() {
        this.toasts = [];
        this.container = null;
        this.root = null;
        this.init();
    }

    init() {
        // Create toast container
        this.container = document.createElement('div');
        this.container.id = 'toast-container';
        this.container.className = 'fixed top-4 right-4 z-50 space-y-2';
        document.body.appendChild(this.container);

        // Create React root
        this.root = createRoot(this.container);
        this.render();
    }

    addToast(message, type = 'info', duration = 5000) {
        const id = Date.now() + Math.random();
        const toast = {
            id,
            message,
            type,
            duration
        };

        this.toasts.push(toast);
        this.render();

        // Auto remove after duration
        if (duration > 0) {
            setTimeout(() => {
                this.removeToast(id);
            }, duration);
        }

        return id;
    }

    removeToast(id) {
        this.toasts = this.toasts.filter(toast => toast.id !== id);
        this.render();
    }

    success(message, duration = 5000) {
        return this.addToast(message, 'success', duration);
    }

    error(message, duration = 7000) {
        return this.addToast(message, 'error', duration);
    }

    info(message, duration = 5000) {
        return this.addToast(message, 'info', duration);
    }

    warning(message, duration = 6000) {
        return this.addToast(message, 'warning', duration);
    }

    render() {
        if (!this.root) return;

        this.root.render(
            <AnimatePresence>
                {this.toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        toast={toast}
                        onClose={() => this.removeToast(toast.id)}
                    />
                ))}
            </AnimatePresence>
        );
    }

    destroy() {
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        this.toasts = [];
        this.container = null;
        this.root = null;
    }
}

const Toast = ({ toast, onClose }) => {
    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return <CheckCircle size={20} className="text-green-600" />;
            case 'error':
                return <AlertCircle size={20} className="text-red-600" />;
            case 'warning':
                return <AlertCircle size={20} className="text-yellow-600" />;
            default:
                return <Info size={20} className="text-blue-600" />;
        }
    };

    const getStyles = () => {
        switch (toast.type) {
            case 'success':
                return 'bg-green-50 border-green-200 text-green-800';
            case 'error':
                return 'bg-red-50 border-red-200 text-red-800';
            case 'warning':
                return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            default:
                return 'bg-blue-50 border-blue-200 text-blue-800';
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 300, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className={`max-w-sm w-full bg-white border-2 rounded-lg shadow-lg p-4 ${getStyles()}`}
        >
            <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                        {toast.message}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="flex-shrink-0 p-1 hover:bg-black hover:bg-opacity-10 rounded-full transition-colors"
                >
                    <X size={16} className="text-gray-500" />
                </button>
            </div>
        </motion.div>
    );
};

// Create singleton instance
const toastManager = new ToastManager();

// Export methods
const toast = {
    success: (message, duration) => toastManager.success(message, duration),
    error: (message, duration) => toastManager.error(message, duration),
    info: (message, duration) => toastManager.info(message, duration),
    warning: (message, duration) => toastManager.warning(message, duration),
    remove: (id) => toastManager.removeToast(id)
};

export default toast;